import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CalendarClock, Sparkles, ArrowRight } from "lucide-react";

/**
 * Expiry-aware renewal banner on the host's event management page.
 *
 * Shows up only when the event is within 14 days of expiring OR has
 * already expired. Routes straight to the paid upgrade flow so the
 * host can self-renew in two clicks:
 *   see banner → click "Renew now & pay" → Stripe/Paystack checkout.
 *
 * Expired state is intentionally more alarming (red + urgent copy) so
 * hosts whose guests can no longer access the gallery notice fast.
 */
const RenewBanner = ({ event }) => {
  const { daysLeft, isExpired, isSoon } = useMemo(() => {
    const exp = event?.expires_at ? new Date(event.expires_at) : null;
    if (!exp || Number.isNaN(+exp)) {
      return { daysLeft: null, isExpired: false, isSoon: false };
    }
    const now = Date.now();
    const diffDays = (exp.getTime() - now) / 86400000;
    return {
      daysLeft: diffDays,
      isExpired: diffDays <= 0,
      isSoon: diffDays > 0 && diffDays <= 14,
    };
  }, [event]);

  if (!isExpired && !isSoon) return null;

  const upgradeUrl = `/dashboard/events/${event.id}/upgrade?mode=renew`;

  if (isExpired) {
    const daysAgo = Math.ceil(Math.abs(daysLeft));
    return (
      <div data-testid="renew-banner-expired"
           className="rounded-3xl bg-gradient-to-r from-red-500 to-coral-500 text-white px-5 py-4 shadow-lg">
        <div className="flex items-start sm:items-center gap-3 flex-wrap">
          <AlertCircle className="w-6 h-6 shrink-0 mt-0.5 sm:mt-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold">Your gallery expired {daysAgo} day{daysAgo === 1 ? "" : "s"} ago</div>
            <div className="text-xs opacity-90 mt-0.5">
              Guests can&apos;t view photos until you renew. Pay now and we&apos;ll instantly extend your event.
            </div>
          </div>
          <Link
            to={upgradeUrl}
            data-testid="renew-banner-expired-cta"
            className="inline-flex items-center gap-1.5 bg-white text-red-600 px-4 py-2 rounded-full text-sm font-bold hover:bg-cream-100 whitespace-nowrap"
          >
            Renew now & pay <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    );
  }

  // isSoon
  const daysInt = Math.ceil(daysLeft);
  const urgent = daysInt <= 3;
  return (
    <div
      data-testid="renew-banner-soon"
      className={`rounded-3xl px-5 py-4 shadow-md ${
        urgent
          ? "bg-gradient-to-r from-coral-400 to-coral-500 text-white"
          : "bg-gradient-to-r from-cream-100 to-coral-50 border border-coral-200 text-navy-700"
      }`}
    >
      <div className="flex items-start sm:items-center gap-3 flex-wrap">
        {urgent ? (
          <CalendarClock className="w-6 h-6 shrink-0 mt-0.5 sm:mt-0" />
        ) : (
          <Sparkles className="w-6 h-6 shrink-0 mt-0.5 sm:mt-0 text-coral-500" />
        )}
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-bold ${urgent ? "" : "text-navy-700"}`}>
            {urgent
              ? `Expires in ${daysInt} day${daysInt === 1 ? "" : "s"} — renew now so guests don't lose access`
              : `Your gallery expires in ${daysInt} days`}
          </div>
          <div className={`text-xs mt-0.5 ${urgent ? "opacity-90" : "text-navy-700/60"}`}>
            Pay to keep the gallery open — we&apos;ll bank any remaining days so you never lose time.
          </div>
        </div>
        <Link
          to={upgradeUrl}
          data-testid="renew-banner-soon-cta"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap ${
            urgent
              ? "bg-white text-coral-500 hover:bg-cream-100"
              : "bg-coral-300 text-white hover:bg-coral-400"
          }`}
        >
          Renew & pay <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
};

export default RenewBanner;
